// Display formats (design system › Content fundamentals › Time and numbers) for one language and time zone.
import { formatClock, formatDuration, spokenDuration } from '../../../packages/core/src/index.ts';
import { dayRange, localParts, weekNumber } from './calendar.ts';
import type { Lang, Translate } from './i18n.ts';

export interface Formats {
  lang: Lang;
  timeZone: string;
  locale: string;
  /** Live clock `H:MM:SS`. */
  clock(ms: number): string;
  /** `H:MM h` from one hour, `45 min` below. */
  duration(ms: number): string;
  /** `H:MM` without unit (stat tiles). */
  hm(ms: number): string;
  /** "38,5" / "38.5" (reports; the unit sits next to it). */
  decimalHours(ms: number, digits?: number): string;
  /** Chart ticks: minutes below one hour, fractional hours above it. */
  chartDuration(hours: number): string;
  /** "1.240 €" / "€1,240". */
  money(cents: number, currency?: string, digits?: number): string;
  /** A number in the locale ("1.240"). */
  number(value: number, digits?: number): string;
  /** 24-hour clock time "13:12". */
  time(t: number): string;
  /** "Freitag, 25. September" */
  weekdayDayMonth(t: number): string;
  /** "Freitag, 25. September 2026" */
  fullDate(t: number): string;
  /** "25. Sep." */
  dayMonthShort(t: number): string;
  /** "25. Sep. 2026" */
  dayMonthYear(t: number): string;
  /** "25.09." / "09/25" */
  dayMonthNumeric(t: number): string;
  /** "Fr., 25." */
  weekdayShortDay(t: number): string;
  /** "Sep." */
  monthShort(t: number): string;
  /** "September 2026" */
  monthYear(t: number): string;
  /** Today: "13:12"; earlier: "25.09. 13:12". */
  started(t: number, now: number): string;
  /** "vor 2 min", "vor 3 h", "vor 2 Tagen". */
  relative(t: number, now: number): string;
  /** "1,2 Mio." / "1.2M". */
  tokens(n: number): string;
  /** "$4.87" */
  usd(micro: number): string;
  /** "1 Stunde 27 Minuten" for screen readers. */
  spoken(ms: number): string;
  week(t: number): number;
  /** "Freitag, 25. September · Woche 39" */
  todayLabel(now: number): string;
}

export function makeFormats(lang: Lang, timeZone: string, t: Translate): Formats {
  const locale = lang === 'de' ? 'de-DE' : 'en-US';
  const cache = new Map<string, Intl.DateTimeFormat>();
  const dt = (key: string, options: Intl.DateTimeFormatOptions) => {
    let f = cache.get(key);
    if (!f) {
      f = new Intl.DateTimeFormat(locale, { timeZone, ...options });
      cache.set(key, f);
    }
    return f;
  };
  const numbers = new Map<string, Intl.NumberFormat>();
  const nf = (key: string, options: Intl.NumberFormatOptions) => {
    let f = numbers.get(key);
    if (!f) {
      f = new Intl.NumberFormat(locale, options);
      numbers.set(key, f);
    }
    return f;
  };
  const time = (v: number) => dt('time', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(v);
  const dayMonthNumeric = (v: number) => {
    const p = localParts(v, timeZone);
    const dd = String(p.day).padStart(2, '0');
    const mm = String(p.month).padStart(2, '0');
    return lang === 'de' ? `${dd}.${mm}.` : `${mm}/${dd}`;
  };
  const weekdayDayMonth = (v: number) => dt('wdm', { weekday: 'long', day: 'numeric', month: 'long' }).format(v);
  return {
    lang, timeZone, locale,
    clock: formatClock,
    duration: formatDuration,
    hm(ms) {
      const minutes = Math.max(0, Math.floor((ms + 30_000) / 60_000));
      return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
    },
    decimalHours: (ms, digits = 1) => nf(`h${digits}`, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(ms / 3_600_000),
    chartDuration: (hours) => nf('chart', { maximumSignificantDigits: 3 }).format(hours < 1 ? hours * 60 : hours) + (hours < 1 ? ' min' : ' h'),
    money: (cents, currency = 'EUR', digits = 0) =>
      nf(`m${currency}${digits}`, { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(cents / 100),
    number: (value, digits = 0) => nf(`n${digits}`, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value),
    time,
    weekdayDayMonth,
    fullDate: (v) => dt('full', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(v),
    dayMonthShort: (v) => dt('dms', { day: 'numeric', month: 'short' }).format(v),
    dayMonthYear: (v) => dt('dmy', { day: 'numeric', month: 'short', year: 'numeric' }).format(v),
    dayMonthNumeric,
    weekdayShortDay: (v) => dt('wsd', { weekday: 'short', day: 'numeric' }).format(v),
    monthShort: (v) => dt('ms', { month: 'short' }).format(v),
    monthYear: (v) => dt('my', { month: 'long', year: 'numeric' }).format(v),
    started(v, now) {
      if (v >= dayRange(now, timeZone).start) return time(v);
      return `${dayMonthNumeric(v)} ${time(v)}`;
    },
    relative(v, now) {
      const minutes = Math.max(0, Math.floor((now - v) / 60_000));
      if (minutes < 1) return t('gerade eben');
      if (minutes < 60) return t('vor %lld min', minutes);
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return t('vor %lld h', hours);
      return t('vor %lld Tagen', Math.floor(hours / 24));
    },
    tokens: (n) => nf('tok', { notation: 'compact', maximumFractionDigits: 1 }).format(n),
    usd: (micro) => new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(micro / 1_000_000),
    spoken: (ms) => spokenDuration(ms, lang),
    week: (v) => weekNumber(v, timeZone),
    todayLabel: (now) => t('%@ · Woche %lld', weekdayDayMonth(now), weekNumber(now, timeZone)),
  };
}
